import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Grid,
  GridItem,
  Card,
  CardBody,
  Image,
  VStack,
  HStack,
  Badge,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  AspectRatio,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  Spacer
} from '@chakra-ui/react';
import {
  FaPlay,
  FaClock,
  FaEye,
  FaGraduationCap,
  FaStar,
  FaUsers,
  FaChartLine
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const DiscoverPlatformPage = () => {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';

  useEffect(() => {
    // Simulate loading demo videos
    setTimeout(() => {
      setVideos([
        {
          id: 1,
          title: "مقدمة عن منصة كوبرا التعليمية",
          description: "تعرف على أهم ميزات منصة كوبرا وكيف تساعد الطلاب على التفوق",
          thumbnail: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
          duration: "5:30",
          views: "15,420",
          category: "تعريفي",
          videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
          featured: true
        },
        {
          id: 2,
          title: "الدروس المرئية التفاعلية",
          description: "شاهد كيف تجعل الدروس المرئية التعلم أكثر متعة وفعالية",
          thumbnail: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
          duration: "8:15",
          views: "12,890",
          category: "تعليمي",
          videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ"
        },
        {
          id: 3,
          title: "الحصص المباشرة مع الأساتذة",
          description: "تفاعل مباشر مع أفضل الأساتذة في تونس",
          thumbnail: "https://images.unsplash.com/photo-1588072432836-e10032774350?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
          duration: "6:45",
          views: "18,750",
          category: "حصص مباشرة",
          videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ"
        },
        {
          id: 4,
          title: "نظام التقييم والاختبارات",
          description: "اكتشف نظام التقييم المتطور وكيف يساعدك على قياس تقدمك",
          thumbnail: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
          duration: "7:20",
          views: "9,630",
          category: "تقييم",
          videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ"
        },
        {
          id: 5,
          title: "قصص نجاح الطلاب",
          description: "استمع لتجارب الطلاب الذين حققوا النجاح مع منصة كوبرا",
          thumbnail: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
          duration: "10:30",
          views: "25,180",
          category: "قصص نجاح",
          videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
          featured: true
        },
        {
          id: 6,
          title: "الموارد التعليمية الشاملة",
          description: "تصفح مكتبة ضخمة من الموارد التعليمية المتوافقة مع المنهج التونسي",
          thumbnail: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
          duration: "4:50",
          views: "11,420",
          category: "موارد",
          videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ"
        }
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    onOpen();
  };

  const getCategoryColor = (category) => {
    const colors = {
      'تعريفي': 'blue',
      'تعليمي': 'green',
      'حصص مباشرة': 'purple',
      'تقييم': 'orange',
      'قصص نجاح': 'pink',
      'موارد': 'teal'
    };
    return colors[category] || 'gray';
  };

  if (loading) {
    return (
      <Box dir="rtl">
        <Header />
        <Container maxW="8xl" py={8}>
          <Flex justify="center" align="center" h="400px">
            <Spinner size="xl" color={primaryColor} />
          </Flex>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box dir="rtl">
      <Header />
      
      {/* Hero Section */}
      <Box bg={`linear-gradient(135deg, ${secondaryColor} 0%, ${primaryColor} 100%)`} py={20}>
        <Container maxW="8xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={6} textAlign="center" color="white">
              <Heading size="2xl" fontFamily="Cairo, sans-serif">
                هل تريد حقاً معرفة قوة منصة كوبرا؟
              </Heading>
              <Text fontSize="xl" maxW="3xl" fontFamily="Cairo, sans-serif">
                اضغط هنا واكتشف بنفسك كيف تغير منصة كوبرا التعليمية مستقبل التعليم في تونس
              </Text>
              <HStack spacing={8} mt={8}>
                <VStack>
                  <Icon as={FaUsers} boxSize={8} color={primaryColor} />
                  <Text fontWeight="bold" fontFamily="Cairo, sans-serif">50,000+</Text>
                  <Text fontSize="sm" fontFamily="Cairo, sans-serif">طالب مسجل</Text>
                </VStack>
                <VStack>
                  <Icon as={FaGraduationCap} boxSize={8} color={primaryColor} />
                  <Text fontWeight="bold" fontFamily="Cairo, sans-serif">80+</Text>
                  <Text fontSize="sm" fontFamily="Cairo, sans-serif">أستاذ خبير</Text>
                </VStack>
                <VStack>
                  <Icon as={FaChartLine} boxSize={8} color={primaryColor} />
                  <Text fontWeight="bold" fontFamily="Cairo, sans-serif">95%</Text>
                  <Text fontSize="sm" fontFamily="Cairo, sans-serif">معدل النجاح</Text>
                </VStack>
              </HStack>
            </VStack>
          </motion.div>
        </Container>
      </Box>

      <Container maxW="8xl" py={12}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <VStack spacing={8} mb={12}>
            <Heading size="lg" color={secondaryColor} textAlign="center" fontFamily="Cairo, sans-serif">
              اكتشف قوة منصة كوبرا من خلال هذه الفيديوهات
            </Heading>
            <Text fontSize="lg" color="gray.600" textAlign="center" maxW="4xl" fontFamily="Cairo, sans-serif">
              شاهد كيف تساعد منصة كوبرا آلاف الطلاب في تونس على تحقيق أحلامهم الأكاديمية
            </Text>
          </VStack>

          {/* Featured Videos */}
          <Box mb={12}>
            <Heading size="md" mb={6} color={secondaryColor} fontFamily="Cairo, sans-serif">
              الفيديوهات المميزة
            </Heading>
            <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={8}>
              {videos.filter(video => video.featured).map((video) => (
                <GridItem key={video.id}>
                  <motion.div
                    whileHover={{ y: -5 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card 
                      bg="white" 
                      boxShadow="lg" 
                      borderRadius="xl"
                      overflow="hidden"
                      _hover={{ boxShadow: '2xl' }}
                      transition="all 0.3s"
                      cursor="pointer"
                      onClick={() => handleVideoClick(video)}
                    >
                      <Box position="relative">
                        <Image 
                          src={video.thumbnail} 
                          alt={video.title}
                          h="250px"
                          w="100%"
                          objectFit="cover"
                        />
                        <Box
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                          bg="rgba(0,0,0,0.7)"
                          borderRadius="full"
                          p={4}
                          _hover={{ bg: primaryColor }}
                          transition="all 0.3s"
                        >
                          <Icon as={FaPlay} color="white" boxSize={6} />
                        </Box>
                        <Badge
                          position="absolute"
                          top={4}
                          right={4}
                          colorScheme={getCategoryColor(video.category)}
                          fontFamily="Cairo, sans-serif"
                        >
                          {video.category}
                        </Badge>
                        <Badge
                          position="absolute"
                          bottom={4}
                          right={4}
                          bg="rgba(0,0,0,0.8)"
                          color="white"
                          fontFamily="Cairo, sans-serif"
                        >
                          <Icon as={FaClock} mr={1} />
                          {video.duration}
                        </Badge>
                      </Box>
                      <CardBody>
                        <VStack align="stretch" spacing={3}>
                          <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
                            {video.title}
                          </Heading>
                          <Text color="gray.600" fontSize="sm" fontFamily="Cairo, sans-serif">
                            {video.description}
                          </Text>
                          <HStack justify="space-between">
                            <HStack>
                              <Icon as={FaEye} color="gray.500" />
                              <Text fontSize="sm" color="gray.500" fontFamily="Cairo, sans-serif">
                                {video.views} مشاهدة
                              </Text>
                            </HStack>
                            <HStack>
                              <Icon as={FaStar} color={primaryColor} />
                              <Text fontSize="sm" color="gray.500" fontFamily="Cairo, sans-serif">
                                مميز
                              </Text>
                            </HStack>
                          </HStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  </motion.div>
                </GridItem>
              ))}
            </Grid>
          </Box>

          {/* All Videos */}
          <Box>
            <Heading size="md" mb={6} color={secondaryColor} fontFamily="Cairo, sans-serif">
              جميع الفيديوهات
            </Heading>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
              {videos.map((video) => (
                <GridItem key={video.id}>
                  <motion.div
                    whileHover={{ y: -3 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card 
                      bg="white" 
                      boxShadow="md" 
                      borderRadius="lg"
                      overflow="hidden"
                      _hover={{ boxShadow: 'lg' }}
                      transition="all 0.3s"
                      cursor="pointer"
                      onClick={() => handleVideoClick(video)}
                      h="100%"
                    >
                      <Box position="relative">
                        <Image 
                          src={video.thumbnail} 
                          alt={video.title}
                          h="180px"
                          w="100%"
                          objectFit="cover"
                        />
                        <Box
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                          bg="rgba(0,0,0,0.7)"
                          borderRadius="full"
                          p={3}
                          _hover={{ bg: primaryColor }}
                          transition="all 0.3s"
                        >
                          <Icon as={FaPlay} color="white" boxSize={4} />
                        </Box>
                        <Badge
                          position="absolute"
                          top={3}
                          right={3}
                          colorScheme={getCategoryColor(video.category)}
                          fontSize="xs"
                          fontFamily="Cairo, sans-serif"
                        >
                          {video.category}
                        </Badge>
                        <Badge
                          position="absolute"
                          bottom={3}
                          right={3}
                          bg="rgba(0,0,0,0.8)"
                          color="white"
                          fontSize="xs"
                          fontFamily="Cairo, sans-serif"
                        >
                          {video.duration}
                        </Badge>
                      </Box>
                      <CardBody>
                        <VStack align="stretch" spacing={2}>
                          <Heading size="sm" color={secondaryColor} fontFamily="Cairo, sans-serif" noOfLines={2}>
                            {video.title}
                          </Heading>
                          <Text color="gray.600" fontSize="xs" fontFamily="Cairo, sans-serif" noOfLines={2}>
                            {video.description}
                          </Text>
                          <HStack justify="space-between" mt={2}>
                            <HStack>
                              <Icon as={FaEye} color="gray.400" boxSize={3} />
                              <Text fontSize="xs" color="gray.500" fontFamily="Cairo, sans-serif">
                                {video.views}
                              </Text>
                            </HStack>
                            {video.featured && (
                              <Badge colorScheme="yellow" fontSize="xs" fontFamily="Cairo, sans-serif">
                                مميز
                              </Badge>
                            )}
                          </HStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  </motion.div>
                </GridItem>
              ))}
            </Grid>
          </Box>

          {/* Call to Action */}
          <Box 
            bg={`linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`}
            borderRadius="2xl"
            p={12}
            mt={16}
            textAlign="center"
          >
            <VStack spacing={6} color="white">
              <Heading size="lg" fontFamily="Cairo, sans-serif">
                مقتنع بقوة منصة كوبرا؟
              </Heading>
              <Text fontSize="lg" maxW="2xl" fontFamily="Cairo, sans-serif">
                انضم إلى آلاف الطلاب الذين يحققون النجاح كل يوم مع منصة كوبرا التعليمية
              </Text>
              <HStack spacing={4}>
                <Button
                  size="lg"
                  bg="white"
                  color={secondaryColor}
                  _hover={{ bg: 'gray.100' }}
                  fontFamily="Cairo, sans-serif"
                >
                  تواصل معنا عبر واتساب
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="white"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  fontFamily="Cairo, sans-serif"
                >
                  اتصل بنا
                </Button>
              </HStack>
            </VStack>
          </Box>
        </motion.div>

        {/* Video Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
          <ModalOverlay bg="blackAlpha.800" />
          <ModalContent bg="white" borderRadius="xl">
            <ModalHeader fontFamily="Cairo, sans-serif">
              {selectedVideo?.title}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedVideo && (
                <VStack spacing={4}>
                  <AspectRatio ratio={16 / 9} w="100%">
                    <iframe
                      src={selectedVideo.videoUrl}
                      title={selectedVideo.title}
                      allowFullScreen
                      style={{ borderRadius: '8px' }}
                    />
                  </AspectRatio>
                  <Box w="100%" textAlign="right">
                    <Text color="gray.600" fontFamily="Cairo, sans-serif">
                      {selectedVideo.description}
                    </Text>
                    <HStack mt={4} justify="space-between">
                      <Badge colorScheme={getCategoryColor(selectedVideo.category)} fontFamily="Cairo, sans-serif">
                        {selectedVideo.category}
                      </Badge>
                      <HStack>
                        <Icon as={FaEye} color="gray.500" />
                        <Text fontSize="sm" color="gray.500" fontFamily="Cairo, sans-serif">
                          {selectedVideo.views} مشاهدة
                        </Text>
                      </HStack>
                    </HStack>
                  </Box>
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Container>
      
      <Footer />
    </Box>
  );
};

export default DiscoverPlatformPage;

